// ==========================================================
// Masking Tape Physics & Line Clipping System
// ==========================================================

export interface Point {
  x: number;
  y: number;
}

export interface TapeSegment {
  id: string;
  start: Point;
  end: Point;
  width: number;
  color: string;
  isPeeling?: boolean;
  peelProgress?: number;
}

export class MaskingTapeManager {
  private tapes: TapeSegment[] = [];
  private activeTapeStart: Point | null = null;
  private currentPointer: Point | null = null;
  private tapeWidth: number = 24;
  private hoveredTapeId: string | null = null;

  public getTapes(): TapeSegment[] {
    return this.tapes;
  }

  public setTapeWidth(width: number) {
    this.tapeWidth = width;
  }

  public setHoveredTape(id: string | null) {
    this.hoveredTapeId = id;
  }

  public getHoveredTape(): string | null {
    return this.hoveredTapeId;
  }

  public startTape(x: number, y: number) {
    this.activeTapeStart = { x, y };
    this.currentPointer = { x, y };
  }

  public updateTape(x: number, y: number) {
    if (this.activeTapeStart) {
      this.currentPointer = { x, y };
    }
  }

  public finishTape(x: number, y: number): TapeSegment | null {
    if (!this.activeTapeStart) return null;

    const dx = x - this.activeTapeStart.x;
    const dy = y - this.activeTapeStart.y;
    const dist = Math.hypot(dx, dy);

    // Minimum length for a tape
    if (dist > 15) {
      const newTape: TapeSegment = {
        id: 'tape_' + Date.now() + '_' + Math.random().toString(36).substring(2, 6),
        start: { ...this.activeTapeStart },
        end: { x, y },
        width: this.tapeWidth,
        color: 'rgba(235, 205, 120, 0.88)' // Washi tape aesthetic
      };
      this.tapes.push(newTape);
      this.activeTapeStart = null;
      this.currentPointer = null;
      return newTape;
    }

    this.activeTapeStart = null;
    this.currentPointer = null;
    return null;
  }

  public cancelActiveTape() {
    this.activeTapeStart = null;
    this.currentPointer = null;
  }

  /**
   * Find closest tape to point within its stroke width + tolerance
   */
  public findTapeAt(x: number, y: number, tolerance: number = 10): TapeSegment | null {
    let closestTape: TapeSegment | null = null;
    let minDistance = Infinity;

    for (const tape of this.tapes) {
      if (tape.isPeeling) continue;
      const dist = this.distToSegment({ x, y }, tape.start, tape.end);
      const threshold = tape.width / 2 + tolerance;

      if (dist <= threshold && dist < minDistance) {
        minDistance = dist;
        closestTape = tape;
      }
    }

    return closestTape;
  }

  /**
   * Peel a single individual tape by ID with peel animation
   */
  public peelSingleTape(tapeId: string, onComplete?: () => void) {
    const tape = this.tapes.find(t => t.id === tapeId);
    if (!tape || tape.isPeeling) {
      if (onComplete) onComplete();
      return;
    }

    tape.isPeeling = true;
    tape.peelProgress = 0;

    const startTime = performance.now();
    const duration = 300; // ms

    const animatePeel = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      tape.peelProgress = progress;

      if (progress < 1) {
        requestAnimationFrame(animatePeel);
      } else {
        // Remove this specific tape from list
        this.tapes = this.tapes.filter(t => t.id !== tapeId);
        if (this.hoveredTapeId === tapeId) {
          this.hoveredTapeId = null;
        }
        if (onComplete) onComplete();
      }
    };

    requestAnimationFrame(animatePeel);
  }

  /**
   * Peel all tapes simultaneously
   */
  public peelAllTapes(onComplete?: () => void) {
    if (this.tapes.length === 0) {
      if (onComplete) onComplete();
      return;
    }

    // Trigger peel animation for all
    this.tapes.forEach(t => {
      t.isPeeling = true;
      t.peelProgress = 0;
    });

    const startTime = performance.now();
    const duration = 380; // ms

    const animatePeel = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);

      this.tapes.forEach(t => {
        t.peelProgress = progress;
      });

      if (progress < 1) {
        requestAnimationFrame(animatePeel);
      } else {
        this.tapes = [];
        this.hoveredTapeId = null;
        if (onComplete) onComplete();
      }
    };

    requestAnimationFrame(animatePeel);
  }

  public clearTapes() {
    this.tapes = [];
    this.activeTapeStart = null;
    this.currentPointer = null;
    this.hoveredTapeId = null;
  }

  public render(ctx: CanvasRenderingContext2D) {
    ctx.save();

    // 1. Draw existing tapes
    for (const tape of this.tapes) {
      this.renderSingleTape(ctx, tape);
    }

    // 2. Draw active dragging preview tape
    if (this.activeTapeStart && this.currentPointer) {
      ctx.save();
      ctx.setLineDash([8, 6]);
      ctx.strokeStyle = 'rgba(235, 205, 120, 0.7)';
      ctx.lineWidth = this.tapeWidth;
      ctx.lineCap = 'butt';
      ctx.beginPath();
      ctx.moveTo(this.activeTapeStart.x, this.activeTapeStart.y);
      ctx.lineTo(this.currentPointer.x, this.currentPointer.y);
      ctx.stroke();

      // Guidelines
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = '#fff';
      ctx.beginPath();
      ctx.arc(this.activeTapeStart.x, this.activeTapeStart.y, 6, 0, Math.PI * 2);
      ctx.arc(this.currentPointer.x, this.currentPointer.y, 6, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    ctx.restore();
  }

  private renderSingleTape(ctx: CanvasRenderingContext2D, tape: TapeSegment) {
    ctx.save();
    
    // Tape line coordinates
    const p1 = tape.start;
    const p2 = tape.end;

    // Angle & length
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const length = Math.hypot(dx, dy);
    const angle = Math.atan2(dy, dx);

    ctx.translate(p1.x, p1.y);
    ctx.rotate(angle);

    // Peeling deformation
    const progress = tape.peelProgress || 0;
    const visibleLength = length * (1 - progress);

    const isHovered = this.hoveredTapeId === tape.id && !tape.isPeeling;

    if (visibleLength > 1) {
      // Tape body with realistic texture pattern
      ctx.fillStyle = tape.color;
      ctx.shadowColor = isHovered ? 'rgba(255, 107, 129, 0.8)' : 'rgba(0, 0, 0, 0.35)';
      ctx.shadowBlur = isHovered ? 14 : 6;
      ctx.shadowOffsetY = isHovered ? 0 : 3;

      ctx.fillRect(0, -tape.width / 2, visibleLength, tape.width);

      // Washi paper fibers/border
      ctx.shadowColor = 'transparent';
      ctx.strokeStyle = isHovered ? '#ff4757' : 'rgba(255, 255, 255, 0.4)';
      ctx.lineWidth = isHovered ? 2 : 1;
      if (isHovered) {
        ctx.setLineDash([6, 4]);
      }
      ctx.strokeRect(0, -tape.width / 2, visibleLength, tape.width);

      // Hover icon indicator
      if (isHovered) {
        ctx.fillStyle = '#ff4757';
        ctx.font = 'bold 12px sans-serif';
        ctx.fillText('✂️ 剥がす', visibleLength / 2 - 20, 4);
      }

      // Rip edge when peeling
      if (progress > 0) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
        ctx.fillRect(visibleLength - 5, -tape.width / 2, 5, tape.width);
      }
    }

    ctx.restore();
  }

  public applyClipMask(_ctx: CanvasRenderingContext2D) {
    if (this.tapes.length === 0) return;
  }

  public isPointOnTape(x: number, y: number): boolean {
    for (const tape of this.tapes) {
      const dist = this.distToSegment({ x, y }, tape.start, tape.end);
      if (dist <= tape.width / 2) return true;
    }
    return false;
  }

  private distToSegment(p: Point, v: Point, w: Point): number {
    const l2 = (v.x - w.x) ** 2 + (v.y - w.y) ** 2;
    if (l2 === 0) return Math.hypot(p.x - v.x, p.y - v.y);
    let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(p.x - (v.x + t * (w.x - v.x)), p.y - (v.y + t * (w.y - v.y)));
  }
}
